import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { useTheme } from "../../hooks/useTheme";
import { Button } from "../ui/Button";
import { Badge } from "../ui/Badge";

const roleLabels: Record<string, string> = {
  admin: "Yönetici",
  operator: "Operatör",
  viewer: "Görüntüleyici",
};

interface TopbarProps {
  onToggleSidebar: () => void;
}

/** Üst bar: mobil menü, tema değiştirici, kullanıcı menüsü. */
export function Topbar({ onToggleSidebar }: TopbarProps) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  return (
    <header className="bg-card border-border flex h-16 items-center justify-between border-b px-4 lg:px-6">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={onToggleSidebar}
          aria-label="Gezinme menüsünü aç/kapat"
        >
          ☰
        </Button>
        <p className="text-muted-foreground hidden text-sm sm:block">
          Ağ cihazı ve uç nokta izleme
        </p>
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={toggle}
          aria-label={theme === "dark" ? "Açık temaya geç" : "Koyu temaya geç"}
        >
          <span aria-hidden="true">{theme === "dark" ? "☀" : "☾"}</span>
        </Button>

        <div className="relative" ref={menuRef}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMenuOpen((open) => !open)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <span className="bg-primary/15 text-primary flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold">
              {(user?.displayName ?? user?.email ?? "?").charAt(0).toUpperCase()}
            </span>
            <span className="max-w-32 truncate">{user?.displayName ?? user?.email}</span>
          </Button>

          {menuOpen ? (
            <div
              role="menu"
              aria-label="Kullanıcı menüsü"
              className="bg-card border-border absolute right-0 z-40 mt-2 w-56 rounded-md border p-2 shadow-lg"
            >
              <div className="px-2 py-1.5">
                <p className="truncate text-sm font-medium">
                  {user?.displayName ?? user?.email}
                </p>
                <p className="text-muted-foreground truncate text-xs">{user?.email}</p>
                <Badge tone="muted" className="mt-1">
                  {roleLabels[user?.role ?? ""] ?? user?.role}
                </Badge>
              </div>
              <div className="border-border mt-2 border-t pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    void logout();
                  }}
                >
                  Çıkış yap
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}